import type { ITemplateNode } from '@ifs-gcp/core';
import { isQuestionNode } from '@ifs-gcp/core/helpers';
import type {
  BaseTemplateElement,
  CardChild,
  QuestionSizes,
  TranslationText,
} from '@ifs-gcp/core/models';

type CardReturnValue = {
  cardTitle: TranslationText;
  cardBody: ITemplateNode<BaseTemplateElement>[];
};

export const getCardProps = (children: CardChild['children']): CardReturnValue => {
  const cardTitle = children.find((e): e is CardChild['children'][0] => e.type === 'card-header')
    ?.children?.[0]?.props?.text;

  if (!cardTitle) {
    throw new Error('[getCardProps]: Could not find "card-header" element');
  }

  const cardBody =
    children.find((e): e is ITemplateNode<CardChild['children'][1]> => e.type === 'card-body')
      ?.children || [];

  return { cardTitle, cardBody };
};

const isQuestionType = <T extends BaseTemplateElement>(element: T): boolean => {
  return element?.type === 'question' || element?.type === 'question-text';
};

export const sizeToNumber = (size: QuestionSizes | undefined = '1/1') => {
  switch (size) {
    case '1/3':
      return 4;
    case '1/2':
      return 6;
    case '2/3':
      return 8;
    default:
      return 12;
  }
};

const fillQuestionsRow = <T extends ITemplateNode<BaseTemplateElement>>(
  children: T[],
  questionsRow: T[],
  childIndex: number,
): number => {
  let newSize = 0;
  let localChildIndex = childIndex;
  while (newSize < 12 && localChildIndex < children.length) {
    const element = children[localChildIndex];
    const size = isQuestionNode(element) ? element.props.size : undefined;
    const nextSize = size ? sizeToNumber(size) : 12;

    if (newSize + nextSize > 12) {
      newSize = 12;
    } else {
      newSize += nextSize;
      questionsRow.push(children[localChildIndex]);
      localChildIndex += 1;
    }
  }

  return localChildIndex;
};

export const groupChildrenBySize = <T extends ITemplateNode<BaseTemplateElement>>(
  children: T[] | undefined | null,
): T[][] => {
  const newQuestions: T[][] = [];

  if (!children?.length) {
    return newQuestions;
  }

  let childIndex = 0;

  const isNonQuestionType = (element: T) =>
    element?.type !== 'question' && element?.type !== 'question-text';

  const processNonQuestionType = (element: T, questionsRow: T[]) => {
    questionsRow.push(element);
    childIndex += 1;
  };

  const processQuestionType = (questionsRow: T[]) => {
    if (!isQuestionType(children[childIndex])) {
      questionsRow.push(children[childIndex]);
      childIndex += 1;
    } else {
      childIndex = fillQuestionsRow(children, questionsRow, childIndex);
    }
  };

  while (childIndex < children.length) {
    const questionsRow: T[] = [];
    const element = children[childIndex];

    if (isNonQuestionType(element)) {
      processNonQuestionType(element, questionsRow);
    } else {
      processQuestionType(questionsRow);
    }

    newQuestions.push(questionsRow);
  }

  return newQuestions;
};
